import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { customerApi, formatApiError, CUSTOMER_TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/layout/Logo";
import PhoneInput from "@/components/PhoneInput";
import NationalityCombobox from "@/components/forms/NationalityCombobox";

/**
 * Two-stage customer registration (Iter-364 · passwordless):
 * Stage 1: collect first_name, last_name, email (required), whatsapp (required), nationality
 *          → POST /auth/customer/register-start → email OTP sent
 * Stage 2: enter 6-digit OTP → POST /auth/customer/verify-otp → token issued, log in.
 */
export default function CustomerRegister() {
  const nav = useNavigate();
  const [stage, setStage] = useState("details"); // "details" | "otp"
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", whatsapp: "", nationality: "" });
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const { setCustomerFromToken } = useAuth();

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitDetails = async (e) => {
    e.preventDefault();
    if (!form.email || !form.whatsapp) {
      toast.error("Both email and WhatsApp/Mobile number are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await customerApi.post("/auth/customer/register-start", form);
      setPendingId(r.data.pending_id);
      setStage("otp");
      toast.success("We sent a 6-digit code to your email.");
      if (r.data.dev_otp) {
        toast.message(`Dev mode OTP: ${r.data.dev_otp}`, { description: "Email delivery not configured yet — showing OTP locally." });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setBusy(true);
    try {
      const r = await customerApi.post("/auth/customer/verify-otp", { pending_id: pendingId, code: otp });
      localStorage.setItem(CUSTOMER_TOKEN_KEY, r.data.token);
      await setCustomerFromToken(r.data.user);
      toast.success("Welcome to Cocoon");
      nav("/portal/book");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!pendingId) return;
    setBusy(true);
    try {
      const r = await customerApi.post("/auth/customer/resend-otp", { pending_id: pendingId });
      toast.success("New code sent to your email.");
      if (r.data.dev_otp) toast.message(`Dev OTP: ${r.data.dev_otp}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1">
        {stage === "details" ? (
          <form onSubmit={submitDetails} className="w-full max-w-sm" data-testid="customer-register-form">
            <Logo variant="mark" to="/" size={56} />
            <h1 className="font-editorial text-4xl mt-8">Create your account</h1>
            <p className="text-sm text-muted-foreground mt-2">One profile. Every branch. Every visit.</p>

            <div className="mt-8 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first_name">First name</Label>
                  <Input id="first_name" data-testid="register-first-name" value={form.first_name} onChange={(e) => change("first_name", e.target.value)} className="mt-1" required />
                </div>
                <div>
                  <Label htmlFor="last_name">Last name</Label>
                  <Input id="last_name" data-testid="register-last-name" value={form.last_name} onChange={(e) => change("last_name", e.target.value)} className="mt-1" required />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                <Input id="email" type="email" data-testid="register-email" value={form.email} onChange={(e) => change("email", e.target.value)} placeholder="you@example.com" className="mt-1" required />
              </div>
              <div>
                <Label htmlFor="whatsapp">WhatsApp/Mobile number <span className="text-destructive">*</span></Label>
                <div className="mt-1">
                  <PhoneInput
                    testid="register-whatsapp"
                    value={form.whatsapp}
                    onChange={(v) => change("whatsapp", v)}
                    defaultCountryCode="ID"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="nationality">Nationality</Label>
                <div className="mt-1">
                  <NationalityCombobox
                    value={form.nationality}
                    onChange={(v) => change("nationality", v)}
                    testId="register-nationality"
                  />
                </div>
              </div>
              {/* Iter-364 · Clean Removal. Password field DELETED — the
                  Guest Portal is passwordless. Guests sign in with a
                  one-time code emailed on each visit. */}
              <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="register-submit">
                {busy ? "Sending code…" : "Send verification code"}
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                Already registered? <Link to="/portal/login" className="text-foreground underline underline-offset-4" data-testid="go-to-login-link">Sign in</Link>
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={submitOtp} className="w-full max-w-sm" data-testid="customer-otp-form">
            <Logo variant="mark" to="/" size={56} />
            <h1 className="font-editorial text-4xl mt-8">Check your email</h1>
            <p className="text-sm text-muted-foreground mt-2">We sent a 6-digit verification code to <span className="text-foreground">{form.email}</span>.</p>

            <div className="mt-8 space-y-4">
              <div>
                <Label htmlFor="otp">Verification code</Label>
                <Input
                  id="otp"
                  data-testid="register-otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  className="mt-1 text-center text-2xl tracking-[0.5em] font-editorial"
                  required
                />
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="register-verify-btn">
                {busy ? "Verifying…" : "Verify & create account"}
              </Button>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <button type="button" className="underline underline-offset-4" onClick={() => setStage("details")} data-testid="otp-back-btn">Back</button>
                <button type="button" className="underline underline-offset-4 hover:text-foreground" onClick={resend} data-testid="otp-resend-btn">Resend code</button>
              </div>
            </div>
          </form>
        )}
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img
          alt="Bali nature"
          className="absolute inset-0 w-full h-full object-cover"
          src="https://images.unsplash.com/photo-1590476490365-e1a8e952fee4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxiYWxpJTIwc3BhJTIwbmF0dXJlfGVufDB8fHx8MTc4NTEyODY1MXww&ixlib=rb-4.1.0&q=85"
        />
      </div>
    </div>
  );
}
